/**
 * Hallazgo #4 (smoke 2026-09-08) — el carrito de la tienda pública conservaba
 * compras de visitas anteriores sin aviso: un cliente podía pagar algo que no
 * pidió hoy. Helpers PUROS (testeables con vitest):
 *  - expira a las 24 h; "visita anterior" = guardado hace más de 30 min.
 *
 * TREN 4 — el carrito pasa de Record<productId,qty> a LÍNEAS
 * (`{ productId, qty, options }`): un "extra" es por-RENGLÓN, dos del mismo
 * producto con distintos extras son dos líneas. Cada línea guarda el SNAPSHOT de
 * las opciones (id+nombre+precio con IVA que vio el cliente) para pintar el
 * carrito tras recargar sin re-consultar; el server sigue siendo la verdad del
 * dinero (recalcula en hold/create_order). Regla #1: un producto SIN
 * modificadores es una sola línea con `options: []` y se comporta idéntico a
 * antes. El formato v2 `{ items, savedAt }` en vuelo se MIGRA a líneas sin
 * opciones (no se descarta) para no romper el carrito de nadie en el deploy.
 */
export interface ChosenOption {
  id: string;
  name: string;
  /** Precio final con IVA de la opción (mismo que muestra la API, regla V163). */
  price: number;
}
export interface CartLine {
  productId: string;
  /** PR2 (V191) — talla/variante elegida; undefined = producto sin desglose. */
  variantId?: string;
  /** Snapshot del nombre de la talla para pintar el carrito sin re-consultar. */
  variantName?: string;
  qty: number;
  /** Opciones de modificador elegidas (ordenadas por id); [] = sin modificadores. */
  options: ChosenOption[];
  /** P4 — nota libre por renglón (mismo campo que order_items.notes del POS). */
  note?: string;
}

export const CART_TTL_MS = 24 * 60 * 60 * 1000;      // 24 h — el carrito muere solo
export const PREVIOUS_VISIT_MS = 30 * 60 * 1000;     // >30 min = visita anterior (banner)

export interface ParsedCart {
  lines: CartLine[];
  /** true = el carrito viene de una VISITA ANTERIOR (banner "Vaciar"). */
  fromPreviousVisit: boolean;
}

/** IDs de opción de una línea (para el backend y la clave). */
export const optionIdsOf = (l: Pick<CartLine, 'options'>): string[] =>
  (l.options ?? []).map((o) => o.id);

/** Clave estable de una línea (producto + talla + opciones ordenadas): merge y key de React.
 *  PR2 — dos tallas del mismo producto son DOS líneas (variantId en la clave). */
export function lineKey(l: Pick<CartLine, 'productId' | 'variantId' | 'options'>): string {
  const opts = optionIdsOf(l).slice().sort();
  return `${l.productId}␟${l.variantId ?? ''}␟${opts.join('␟')}`;
}

export function parseStoredCart(raw: string | null, now: number): ParsedCart | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.savedAt !== 'number') return null;
    if (now - parsed.savedAt > CART_TTL_MS) return null; // expirado (24 h)

    let lines: CartLine[] | null = null;
    if (Array.isArray(parsed.lines)) {
      lines = sanitizeLines(parsed.lines);                    // formato v3 (líneas)
    } else if (parsed.items && typeof parsed.items === 'object') {
      // Formato v2 (Record productId->qty): migrar a líneas SIN opciones. Rule #1:
      // el carrito sin modificadores en vuelo sobrevive el deploy sin cambiar nada.
      lines = sanitizeLines(
        Object.entries(parsed.items as Record<string, unknown>)
          .map(([productId, qty]) => ({ productId, qty: Number(qty), options: [] })),
      );
    }
    if (!lines || lines.length === 0) return null;
    return { lines, fromPreviousVisit: now - parsed.savedAt > PREVIOUS_VISIT_MS };
  } catch { return null; }
}

export function serializeCart(lines: CartLine[], now: number): string {
  return JSON.stringify({ lines: sanitizeLines(lines), savedAt: now });
}

/** Borra TODOS los carritos guardados de la tienda de un slug (pago exitoso). */
export function clearCartsForSlug(storage: Pick<Storage, 'key' | 'length' | 'removeItem'>, slug: string): void {
  const prefix = `ag_store_cart.${slug}.`;
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) doomed.push(k);
  }
  for (const k of doomed) storage.removeItem(k);
}

function sanitizeOptions(raw: unknown): ChosenOption[] {
  if (!Array.isArray(raw)) return [];
  const byId = new Map<string, ChosenOption>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id : '';
    if (!id) continue;
    const price = Number(r.price);
    byId.set(id, {
      id,
      name: typeof r.name === 'string' ? r.name : '',
      price: Number.isFinite(price) && price >= 0 ? price : 0,
    });
  }
  return [...byId.values()].sort((a, b) => a.id.localeCompare(b.id));
}

/** qty entera >0; opciones saneadas; funde líneas con la MISMA clave sumando qty. */
function sanitizeLines(raw: unknown[]): CartLine[] {
  const byKey = new Map<string, CartLine>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const productId = typeof r.productId === 'string' ? r.productId : '';
    if (!productId) continue;
    const qty = Math.floor(Number(r.qty));
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const note = typeof r.note === 'string' ? r.note.slice(0, 280) : undefined;
    const variantId = typeof r.variantId === 'string' && r.variantId ? r.variantId : undefined;
    const variantName = typeof r.variantName === 'string' ? r.variantName : undefined;
    const line: CartLine = {
      productId, qty, options: sanitizeOptions(r.options),
      ...(variantId ? { variantId, ...(variantName ? { variantName } : {}) } : {}),
      ...(note ? { note } : {}),
    };
    const k = lineKey(line);
    const prev = byKey.get(k);
    if (prev) { prev.qty += qty; if (!prev.note && note) prev.note = note; }
    else byKey.set(k, line);
  }
  return [...byKey.values()];
}
